import type { CSSProperties, JSX } from 'react'
import type { AccountRow, ConfidenceLabel } from '../api.js'
import { T } from '../tokens.js'

/**
 * The shared display vocabulary: confidence, enrollment status, flags,
 * characterization, and the kW figure itself.
 *
 * **This module is where three of the eleven display rules stop being prose.**
 *
 * *Rule 1 — no estimate is ever displayed without its confidence label.* The
 * enforceable form of that is an API in which the violation does not compile,
 * so `KwFigure` takes a **required** `confidence`, and the only way to render
 * the figure without a pill beside it is to say `label="adjacent"`, which then
 * *requires* the id of the element carrying the label. There is no third way to
 * spell it, and a lint rule or a review habit is not equivalent to a type
 * error.
 *
 * *Rule 3 — three characterization states, three treatments.* `recorded`,
 * `undetermined` and `none` are three different facts about an account and
 * `CharacterizationLabel` draws them three different ways.
 *
 * *Rule 4 — absence never looks like deficiency.* The `none` case is plain grey
 * body text reading `Not characterized`. No amber, no icon, no border, no "add
 * characterization" affordance. 212 of the 313 accounts on the seeded book are
 * uncharacterized and always will be; that is the normal state of an account,
 * not a gap in the record. The mock's own filter helper text says it: *"A
 * filter, not a defect. Most accounts are uncharacterized and always will be."*
 *
 * Every colour comes from `tokens.ts`. The pill geometry — 999px radius, 10px
 * uppercase, 0.07–0.08em tracking, 1.5px borders — is the mock's, read out of
 * its `confStyleFor` / `statusStyleFor` helpers rather than remembered.
 */

const pillBase: CSSProperties = {
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: T.radius.pill,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  whiteSpace: 'nowrap',
  // A transparent border on the filled pills so that the outlined ones (Low
  // confidence, every status) are the same height rather than 3px taller.
  border: '1.5px solid transparent',
}

const CONFIDENCE_LABELS = { high: 'High', medium: 'Medium', low: 'Low' } as const

/**
 * The three confidence treatments, from the mock's `confStyleFor`.
 *
 * Low is deliberately the *quiet* one — white, dashed, body-coloured — rather
 * than a red or amber alarm. A Low-confidence estimate is still an estimate
 * worth calling on; it is an instruction about how to talk about the number,
 * not a defect in the account.
 */
const CONFIDENCE_STYLES: Record<ConfidenceLabel, CSSProperties> = {
  high: { background: T.color.green, color: '#fff' },
  medium: { background: T.color.amber, color: T.color.onAmber },
  low: { background: '#fff', color: T.color.body, border: `1.5px dashed ${T.color.dashedBorder}` },
}

/**
 * `wording="full"` spells the word out: `Medium confidence`, not `Medium`.
 *
 * It exists for the one surface whose reader is not a rep. On the drill-down
 * the pill sits under a `Confidence` micro-label and one word is enough; on the
 * presentation screen it sits beside a 62px kW figure with nothing to say what
 * kind of "Medium" is meant, and a customer can read a bare chip as a size
 * band. The one-pager — the customer-facing document rendered from the same
 * projection — already prints `Medium confidence` (`CONFIDENCE_LABEL`,
 * `routes/onepager.ts`), so this is the screen matching the paper rather than
 * a new opinion, which matters when a rep has both in front of a customer.
 *
 * `short` is the default so that every existing call site is unchanged, and
 * neither form is a *reason* — the reasons stay internal (display rule 7 and
 * the server's own projection, which sends the label and drops
 * `confidence.reasons`).
 */
export function ConfidencePill(
  { confidence, wording = 'short' }: { confidence: ConfidenceLabel; wording?: 'short' | 'full' },
): JSX.Element {
  return (
    <span data-testid="confidence" style={{ ...pillBase, ...CONFIDENCE_STYLES[confidence] }}>
      {wording === 'full' ? `${CONFIDENCE_LABELS[confidence]} confidence` : CONFIDENCE_LABELS[confidence]}
    </span>
  )
}

export type EnrollmentStatus = AccountRow['enrollmentStatus']

const STATUS_LABELS: Record<EnrollmentStatus, string> = {
  enrolled: 'Enrolled',
  not_enrolled: 'Not enrolled',
  declined: 'Declined',
}

const STATUS_STYLES: Record<EnrollmentStatus, CSSProperties> = {
  enrolled: { borderColor: T.color.green, color: T.color.enrolledInk, background: T.tint.enrolled },
  declined: { borderColor: T.color.red, color: T.color.declinedInk, background: T.tint.red },
  not_enrolled: { borderColor: T.color.navy, color: T.color.navy, background: T.tint.blue },
}

/**
 * Enrollment status, with room for the detail that goes with it.
 *
 * `detail` exists because of the one thing the mock draws without explaining:
 * Sky Harbor shows `94` in Curtailable kW while its status reads
 * `Enrolled · nominated 95 kW`. Those are two different facts — the current
 * estimate and the nomination of record — and the plan is explicit that both
 * are kept and labelled rather than reconciled into one number. Reconciling
 * them would either overwrite a contractual figure with an estimate or make the
 * estimate look wrong.
 */
export function StatusPill({ status, detail }: { status: EnrollmentStatus; detail?: string }): JSX.Element {
  return (
    <span data-testid="status" style={{ ...pillBase, ...STATUS_STYLES[status], letterSpacing: '0.07em' }}>
      {detail === undefined ? STATUS_LABELS[status] : `${STATUS_LABELS[status]} · ${detail}`}
    </span>
  )
}

/**
 * The internal-only marker, from the mock's list cell and its drill-down
 * banner.
 *
 * Red, and unlike the characterization `none` case it *is* meant to read as
 * "stop and look" — a cohort outlier at the 94th percentile is a number to
 * verify before it is quoted to a customer. `title` carries the flag codes so
 * the pill itself can stay one word wide in a table cell.
 *
 * It says `Flag`, never the flag's message: flag text is internal, and rule 7
 * keeps internal text out of customer-facing surfaces entirely. Presentation
 * mode and the one-pager render no flags at all (the server's own projection
 * strips them), so this component never reaches those screens.
 */
export function FlagPill({ label = 'Flag', title }: { label?: string; title?: string }): JSX.Element {
  return (
    <span
      data-testid="flag"
      title={title}
      style={{
        ...pillBase,
        background: T.color.red,
        color: '#fff',
        padding: '3px 10px',
        fontSize: 9.5,
      }}
    >
      {label}
    </span>
  )
}

/**
 * The three characterization states of display rule 3.
 *
 * The union is the point: `recorded` cannot be spelled without a building type
 * and the other two cannot be spelled with one.
 */
export type Characterization =
  | { state: 'recorded'; buildingType: string }
  | { state: 'undetermined'; buildingType: null }
  | { state: 'none'; buildingType: null }

/**
 * The two fields the API sends, read the way the query itself reads them.
 *
 * `characterized` is "an approved characterization row exists", independent of
 * whether it names a building type — `query/accounts.ts` is explicit that a row
 * with a NULL `building_type` is *not* the same as no row, "when in fact one
 * was reviewed and approved with no type recorded". That is exactly display
 * rule 3's middle state, so the mapping is: a row with a type is `recorded`, a
 * row without one is `undetermined`, and no row at all is `none`.
 *
 * Deriving it in one place rather than in each screen is what keeps the three
 * states from collapsing back into two, which is the failure rule 3 names.
 */
export function characterizationOf(
  account: Pick<AccountRow, 'buildingType' | 'characterized'>,
): Characterization {
  if (!account.characterized) return { state: 'none', buildingType: null }
  if (account.buildingType === null) return { state: 'undetermined', buildingType: null }
  return { state: 'recorded', buildingType: account.buildingType }
}

export const UNDETERMINED_LABEL = 'Undetermined — someone looked and could not tell'
export const NOT_CHARACTERIZED_LABEL = 'Not characterized'

/**
 * `big_box_retail` as `Big box retail`.
 *
 * **The API sends the configured building type's *id*, not its label.**
 * `config.characterization` carries both — `{ id: 'big_box_retail', label:
 * 'Big-box retail anchor' }` — and no route exposes the configuration document
 * to a screen, so the label is unreachable from here. Task 11 wrote this
 * component against a caller that already had the label; Task 12 is the first
 * caller, and it does not.
 *
 * The choice is between presenting the id as words and hardcoding the
 * program's label table in the browser. This is the one that cannot go stale:
 * a new building type reads sensibly rather than falling through to its raw
 * id, and a copy of the table would be wrong the first time somebody edited
 * the configuration and right about nothing in the meantime. It is also a
 * no-op on a string that is already a label, so the day a config route exists,
 * passing the real label through needs no change here.
 *
 * Exported since the derivation accordion (Task 16) became its second caller:
 * the factor-lookup key names the building type the row was chosen for, and a
 * second hand-written id-to-words rule in this package is one too many.
 */
export function asWords(buildingType: string): string {
  const spaced = buildingType.replaceAll('_', ' ').trim()
  if (spaced === '') return buildingType
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function CharacterizationLabel(props: Characterization): JSX.Element {
  if (props.state === 'recorded') {
    return (
      <span data-testid="characterization" style={{ color: T.color.ink, fontWeight: T.weight.body }}>
        {asWords(props.buildingType)}
      </span>
    )
  }
  if (props.state === 'undetermined') {
    // Amber, because somebody spent time on this account and came back without
    // an answer — that is a fact about the record worth seeing.
    return (
      <span data-testid="characterization" style={{ color: T.color.undetermined, fontWeight: T.weight.body }}>
        {UNDETERMINED_LABEL}
      </span>
    )
  }
  // Grey, and nothing else. See the module docblock: this is the normal state
  // of two thirds of the book, and styling it as a gap would put a to-do list
  // of 212 items in front of a rep who has no way to clear it and no reason to.
  return (
    <span data-testid="characterization" style={{ color: T.color.micro, fontWeight: T.weight.body }}>
      {NOT_CHARACTERIZED_LABEL}
    </span>
  )
}

/**
 * Thousands-grouped to one decimal place, by hand.
 *
 * Both halves matter. **By hand** because `Number.prototype.toLocaleString` is
 * locale- and ICU-dependent, so the same figure renders differently on two
 * machines. **One decimal** because that is what
 * `packages/server/src/http/routes/onepager.ts` prints, and the one-pager is
 * the document a rep hands the customer while the screen is still open: 11.2 kW
 * on paper and 11 kW on the screen is the rep's problem to explain, in front of
 * the customer.
 *
 * This duplicates the server's `formatKw` deliberately — the web package has no
 * reference to the server project, by design, so that nothing in the SPA can
 * import a server module and end up computing. A shared formatter is not worth
 * that door. `Pill.test.tsx` pins the rule against the one-pager's.
 */
export function formatKw(value: number): string {
  return `${grouped(value, 1)} kW`
}

/**
 * Thousands-grouped to a fixed number of places, character for character the
 * server's own `grouped` (`http/routes/onepager.ts`). Exported because the
 * targeting list needs the same rule for money, and two hand-written copies of
 * a grouping regex inside one package is one too many.
 */
export function grouped(value: number, places: number): string {
  const fixed = Math.abs(value).toFixed(places)
  const dot = fixed.indexOf('.')
  const whole = dot === -1 ? fixed : fixed.slice(0, dot)
  const fraction = dot === -1 ? '' : fixed.slice(dot)
  return `${value < 0 ? '-' : ''}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction}`
}

/**
 * `$30,842.64` — two places, exactly as the one-pager prints the same figure.
 *
 * `formatKw`'s argument applied to money, unchanged: the one-pager is the
 * document a rep hands the customer *while a screen is still open*, and
 * `$30,843` on the screen beside `$30,842.64` on the paper is a question the rep
 * has to answer in front of the customer. The mock rounds to whole dollars; the
 * mock has no one-pager.
 *
 * It lived in `screens/TargetingList.tsx` until the drill-down became its second
 * caller. A formatter imported from a sibling *screen* is a dependency between
 * two things that have nothing to do with each other; it belongs beside
 * `formatKw` and `grouped`, which are here for the same reason.
 */
export function formatMoney(value: number): string {
  return `$${grouped(value, 2)}`
}

/** `2026-05-12` as `12 May 2026`, character for character the one-pager's `formatDate`. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

export function formatDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (match === null) return value
  const month = Number(match[2])
  return `${Number(match[3])} ${MONTHS[month - 1] ?? month} ${match[1]}`
}

/**
 * Hour `17` as `5:00pm`, hour `22` as `10:00pm` — the one-pager's `formatHour`,
 * transcribed for the same reason `formatKw` and `formatMoney` are.
 *
 * Prose, not a chart label: `ProfileChart` writes `5PM` because it has 56 units
 * of width per hour and no room for anything longer. A sentence a rep reads
 * aloud to a customer gets the full clock time.
 */
export function formatHour(hour: number): string {
  const normalized = ((Math.trunc(hour) % 24) + 24) % 24
  const twelve = normalized % 12 === 0 ? 12 : normalized % 12
  return `${twelve}:00${normalized < 12 ? 'am' : 'pm'}`
}

type KwFigureProps = {
  kw: number
  confidence: ConfidenceLabel
  /**
   * `kpi` is the drill-down's 44px KPI panel figure, `hero` the 38px headline,
   * `inline` inherits its context's type size, and `presentation` is the 62px
   * figure on the customer screen — the largest number this application draws,
   * and the one a customer is looking at while a rep reads it aloud.
   */
  size?: 'kpi' | 'hero' | 'inline' | 'presentation'
  /**
   * `discounted` greys the figure — the targeting list's Weighted column on a
   * Low account. `navy` is the KPI panel's recommended-nomination figure, which
   * the mock draws in brand navy on the blue tint rather than in ink. `red` is
   * a figure produced by a **scenario** rather than by the estimate of record
   * (display rule 2), and it is a tone rather than a caller's inline colour so
   * that every scenario figure on the page is the same red.
   *
   * `warn` is a figure recomputed for a **different day** — also not the
   * estimate of record, and deliberately not the same colour as a scenario:
   * two states sharing one treatment are one state as far as the reader is
   * concerned. It is `warnInk` rather than the brand amber because the figure
   * sits at 44px on the `stale` tint, where `#FBB040` is not readable.
   */
  tone?: 'ink' | 'discounted' | 'navy' | 'red' | 'warn'
  testId?: string
} & (
  | { label?: 'pill' }
  /**
   * The confidence label is rendered by a sibling — a table row's own
   * Confidence cell — rather than by this component. The id is required and is
   * wired to `aria-describedby`, so "it is over there" has to name the element
   * it means, and a screen reader on the figure hears the label too.
   */
  | { label: 'adjacent'; confidenceLabelId: string }
)

/**
 * A kW figure. **Display rule 1 lives in this signature.**
 *
 * `confidence` is required and there is no variant that drops the label:
 * `label="pill"` draws one, `label="adjacent"` points at one. Rendering a bare
 * kW figure in this SPA means not using this component, which is a thing a
 * reviewer can see in a diff — unlike a missing pill, which is a thing nobody
 * sees until a customer is looking at a screen.
 */
const KW_SIZES = { kpi: 44, hero: 38, inline: 'inherit', presentation: 62 } as const
const KW_TONES = {
  ink: T.color.ink, discounted: T.color.micro, navy: T.color.navy, red: T.color.red,
  warn: T.color.warnInk,
} as const

export function KwFigure(props: KwFigureProps): JSX.Element {
  const { kw, confidence, size = 'inline', tone = 'ink' } = props
  const adjacent = props.label === 'adjacent' ? props.confidenceLabelId : undefined
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
      <span
        data-testid={props.testId ?? 'kw'}
        data-kw-size={size}
        // Read by the tests instead of the computed colour: jsdom answers most
        // computed style queries with `''` or a default, and three separate
        // classes of test on this campaign passed against gutted code by
        // measuring the environment. "This figure is a scenario's" is a fact
        // about the figure, so it is in the markup rather than only in a hex.
        data-kw-tone={tone}
        data-kw={kw}
        aria-describedby={adjacent}
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: KW_SIZES[size],
          fontWeight: size === 'inline' ? T.weight.semi : T.weight.display,
          color: KW_TONES[tone],
        }}
      >
        {formatKw(kw)}
      </span>
      {adjacent === undefined ? <ConfidencePill confidence={confidence} /> : null}
    </span>
  )
}
